import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Grid,
  MenuItem,
  TextField,
  Typography,
  Divider,
} from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { FastField } from "formik";
import { getCatalogue } from "../../api/common/catalogues.ts";
import { LGNumber } from "../../atoms/Inputs/LGNumber.tsx";
import DropdownSelector from "../../atoms/Common/DropdownSelector.tsx";
import LabeledCheckbox from "../../atoms/Inputs/LabeledCheckbox.tsx";
import { getProductCategoryList } from "../../api/products";
import { createEstimateItem, updateEstimateItem, getEstimateItemById } from "../../api/quotations/estimateItems.ts";
import { useSnack } from "../../services/SnakbarProvider.js";
import { handleError } from "../../services/errorHandler.js";

interface ProcessProp {
  name: string;
  label: string;
  type: string;
  fetchFunction?: string;
  keyField?: string;
}

interface EstimateItemFormProps {
  estimateId: string;
  initialValues?: {
    name: string;
    quantity: number;
    productCategoryId: number;
    width: number;
    height: number;
    thickness: number;
    thicknessUnit: string;
    description?: string;
    processes?: string[];
    processConfigs?: Record<string, Record<string, any>>;
    isImported: boolean;
    productCost: number;
  };
  onSubmit: (values: any) => void;
  id?: number;
}

// New component for imported products
const QuotingPlasticItem: React.FC<{ values: any; setFieldValue: (field: string, value: any) => void }> = ({ values, setFieldValue }) => {
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h6">Datos del producto importado</Typography>
      </Grid>
      <Grid item xs={6}>
        <FastField
          name="name"
          label="Nombre del producto"
          as={TextField}
          fullWidth
        />
      </Grid>
      <Grid item xs={6}>
        <FastField
          name="productCost"
          label="Precio"
          as={LGNumber}
          fullWidth
        />
      </Grid>
      <Grid item xs={6}>
        <FastField
          name="quantity"
          label="Cantidad"
          type="number"
          as={LGNumber}
          fullWidth
        />
      </Grid>
      <Grid item xs={12}>
        <FastField
          name="description"
          label="Descripción o Especificaciones"
          multiline
          rows={4}
          as={TextField}
          fullWidth
        />
      </Grid>
    </Grid>
  );
};

const validationSchema = Yup.object().shape({
  isImported: Yup.boolean(),
  name: Yup.string().required("El nombre es requerido"),
  quantity: Yup.number()
    .when('isImported', {
      is: false,
      then: (schema) => schema.required("La cantidad es requerida").min(1, "La cantidad debe ser mayor a 0"),
    }),
  productCost: Yup.number()
    .when('isImported', {
      is: true,
      then: (schema) => schema.required("El precio es requerido").min(0, "El precio debe ser mayor o igual a 0"),
    }),
  productCategoryId: Yup.number()
    .when('isImported', {
      is: false,
      then: (schema) => schema.required("La categoría es requerida"),
    }),
  description: Yup.string(),
  processes: Yup.array().of(Yup.string()),
});

const getThicknessUnit = () => {
  return getCatalogue({ catalogueName: "THICKNESS_UNIT" });
};

const getVarnishType = () => {
  return getCatalogue({ catalogueName: "VARNISH_TYPE" });
};

const getLaminationType = () => {
  return getCatalogue({ catalogueName: "LAMINATION_TYPE" });
};

const functionMap = {
  productCategory: getProductCategoryList,
  thicknessUnit: getThicknessUnit,
  varnishType: getVarnishType,
  laminationType: getLaminationType,
};

const EstimateItemForm: React.FC<EstimateItemFormProps> = ({
  estimateId,
  initialValues = {
    name: "",
    quantity: 1,
    productCategoryId: 0,
    description: "",
    processes: [],
    processConfigs: {},
    isImported: false,
    productCost: 0,
    width: 0,
    height: 0,
    thickness: 0,
    thicknessUnit: "",
  },
  onSubmit,
  id,
}) => {  
  const formMode = id ? "edit" : "insert";
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(initialValues);
  const [processes, setProcesses] = useState<any[]>([]);
  const [productCategories, setProductCategories] = useState<any[]>([]);
  const { openSnack } = useSnack();

  const fetchProcesses = async () => {
    setLoading(true);
    const res = await getCatalogue({ catalogueName: "ESTIMATE_PROCESS" });
    setProcesses(res.data);
    setLoading(false);
  };

  const fetchProductCategories = async () => {
    try {
      const res = await getProductCategoryList({ pageSize: 1000 });
      setProductCategories(res.data);
    } catch (error) {
      openSnack(handleError(error), "error");
    }
  };

  useEffect(() => {
    fetchProcesses();
    fetchProductCategories();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (formMode === "edit" && id) {
        setLoading(true);
        try {
          const res = await getEstimateItemById(estimateId, id);
          const itemData = res.data;
          
          // Transform the data to match form structure
          const transformedData = {
            name: itemData.name || "",
            quantity: itemData.quantity || 1,
            productCategoryId: itemData.productCategoryId || 0,
            description: itemData.description || "",
            isImported: itemData.isImported || false,
            productCost: itemData.productCost || 0,
            width: itemData.attributes?.width || 0,
            height: itemData.attributes?.height || 0,
            thickness: itemData.attributes?.thickness ? parseFloat(itemData.attributes.thickness) || 0 : 0,
            thicknessUnit: itemData.attributes?.thickness ? itemData.attributes.thickness.replace(/[0-9.]/g, '') || "" : "",
            processes: itemData.processes?.map(p => p.processId) || [],
            processConfigs: itemData.processes?.reduce((acc, p) => {
              acc[p.processId] = p.configValues || {};
              return acc;
            }, {}) || {},
          };
          
          setFormData(transformedData);
        } catch (error) {
          openSnack(handleError(error), "error");
        } finally {
          setLoading(false);
        }
      } else {
        setFormData(initialValues);
      }
    };

    fetchData();
  }, [formMode, id, estimateId]);

  const getProcessProps = (processId: string): ProcessProp[] => {
    const process = processes.find((p) => p.id === processId);
    if (!process?.props) return [];
    try {
      return process.props;
    } catch {
      return [];
    }
  };

  const handleSubmit = async (values: any) => {
    setLoading(true);

    let processedThickness = values.thickness;
    if (values.thicknessUnit === 'MM' && processedThickness.includes('.')) {
      const numericValue = parseFloat(processedThickness);
      processedThickness = Math.round(numericValue * 100).toString();
    }

    const payload = {
      ...values,
      attributes: {
        width: values.width,
        height: values.height,
        thickness: `${processedThickness}${values.thicknessUnit}`,
      },
      processes: values.processes.map((processId: string) => ({
        processId,
        configValues: values.processConfigs[processId],
      })),
    };
    
    try {
      let res;
      if (formMode === "insert") {
        res = await createEstimateItem(estimateId, payload);
        openSnack("Componente guardado exitosamente", "success");
      } else {
        res = await updateEstimateItem(estimateId, id!, payload);
        openSnack("Componente actualizado exitosamente", "success");
      }
      onSubmit(res.data);
    } catch (error) {
      openSnack(handleError(error), "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      {loading ? (
        <CircularProgress />
      ) : (
        <Formik
          initialValues={formData}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ handleSubmit, values, setFieldValue }) => (
            <Form>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.isImported}
                        onChange={(e) => {
                          setFieldValue("isImported", e.target.checked);
                          if (e.target.checked) {
                            // Reset non-imported fields when switching to imported
                            setFieldValue("quantity", 1);
                            setFieldValue("processes", []);
                            setFieldValue("processConfigs", {});
                            
                            // Set productCategoryId to "PIEZA PLASTICA"
                            const plasticCategory = productCategories.find(
                              (cat) => cat.code === "PLS"
                            );
                            if (plasticCategory) {
                              setFieldValue("productCategoryId", plasticCategory.id);
                            }
                          }
                        }}
                      />
                    }
                    label="Producto Importado?"
                  />
                </Grid>

                {values.isImported ? (
                  <Grid item xs={12}>
                    <QuotingPlasticItem values={values} setFieldValue={setFieldValue} />
                  </Grid>
                ) : (
                  <>
                    <Grid item xs={8}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Typography variant="h6">Datos del componente</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <FastField
                            name="name"
                            label="Nombre del componente"
                            as={TextField}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <FastField
                            name="quantity"
                            label="Cantidad"
                            type="number"
                            as={LGNumber}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <FastField
                            name="productCategoryId"
                            label="Material"
                            as={DropdownSelector}
                            fetchFunction={getProductCategoryList}
                            fetchParams={{ pageSize: 1000 }}
                            optionLabel={["code", "name"]}
                            optionSeparator=" | "
                          />
                        </Grid>

                        <Grid item xs={3}>
                          <FastField
                            name="width"
                            label="Ancho (cm)"
                            type="number"
                            as={LGNumber}
                            fullWidth
                          />
                        </Grid>

                        <Grid item xs={3}>
                          <FastField
                            name="height"
                            label="Alto (cm)"
                            type="number"
                            as={LGNumber}
                            fullWidth
                          />
                        </Grid>

                        <Grid item xs={3}>
                          <FastField
                            name="thickness"
                            label="Espesor"
                            as={TextField}
                            fullWidth
                          />
                        </Grid>

                        <Grid item xs={3}>
                          <FastField
                            name="thicknessUnit"
                            label="Unidad de espesor"
                            as={DropdownSelector}
                            fetchFunction={getThicknessUnit}
                            optionLabel="name"
                            keyField="code"
                            fullWidth
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <FastField
                            name="description"
                            label="Descripción o Especificaciones"
                            multiline
                            rows={4}
                            as={TextField}
                            fullWidth
                          />
                        </Grid>

                        {/* Process-specific form fields */}
                        {values.processes?.map((processId) => {
                          const processProps = getProcessProps(processId);
                          if (processProps.length === 0) return null;

                          return (
                            <React.Fragment key={processId}>
                              <Grid item xs={12}>
                                <Divider sx={{ my: 2 }} />
                                <Typography variant="h6" sx={{ mb: 2 }}>
                                  Configuración de{" "}
                                  {processes.find((p) => p.id === processId)?.name}
                                </Typography>
                              </Grid>
                              {processProps.map((prop) => (
                                <Grid item xs={4} key={prop.name}>
                                  <FastField
                                    name={`processConfigs.${processId}.${prop.name}`}
                                    label={prop.label}
                                    type={prop.type}
                                    as={
                                      prop.type === "Dropdown"
                                        ? DropdownSelector
                                        : prop.type === "number"
                                        ? LGNumber
                                        : prop.type === "boolean"
                                        ? LabeledCheckbox
                                        : TextField
                                    }
                                    fetchFunction={
                                      prop.type === "Dropdown"
                                        ? functionMap[prop.fetchFunction ?? ""]
                                        : undefined
                                    }
                                    keyField={prop.type === "Dropdown" ? prop.keyField : undefined}
                                    fullWidth
                                  />
                                </Grid>
                              ))}
                            </React.Fragment>
                          );
                        })}
                      </Grid>
                    </Grid>

                    <Grid item xs={4}>
                      <Grid item xs={12}>
                        <Typography variant="h6">Procesos</Typography>
                      </Grid>

                      <Grid item xs={12} sx={{ mt: 2 }}>
                        <FastField name="processes">
                          {({ field, form }: any) => (
                            <Box>
                              {loading ? (
                                <CircularProgress />
                              ) : (
                                <FormGroup>
                                  {processes?.map((process) => (
                                    <FormControlLabel
                                      key={process.id}
                                      control={
                                        <Checkbox
                                          checked={field.value?.includes(
                                            process.id
                                          )}
                                          onChange={(e) => {
                                            const isChecked = e.target.checked;
                                            const currentProcesses =
                                              field.value || [];

                                            if (isChecked) {
                                              form.setFieldValue("processes", [
                                                ...currentProcesses,
                                                process.id,
                                              ]);
                                            } else {
                                              form.setFieldValue(
                                                "processes",
                                                currentProcesses.filter(
                                                  (id: string) => id !== process.id
                                                )
                                              );
                                              // Remove process configs when unchecking
                                              const newConfigs = {
                                                ...form.values.processConfigs,
                                              };
                                              delete newConfigs[process.id];
                                              form.setFieldValue(
                                                "processConfigs",
                                                newConfigs
                                              );
                                            }
                                          }}
                                        />
                                      }
                                      label={process.name}
                                    />
                                  ))}
                                </FormGroup>
                              )}
                            </Box>
                          )}
                        </FastField>
                      </Grid>
                    </Grid>
                  </>
                )}

                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Button type="submit" variant="contained" color="primary" disabled={loading}>
                    {formMode === "edit" ? "Actualizar" : "Guardar"}
                  </Button>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      )}
    </Box>
  );
};

export default EstimateItemForm;
